import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  AttachOIDC,
  OIDCOptions,
  capitalize,
  queries,
  service,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EditorIcon,
  Form,
  HelpIcon,
  InputField,
  InputFormField,
  LinkIcon,
  PasswordFormField,
  SelectFormField,
  SelectOption,
  notifyError,
  notifySuccess,
} from "@theprelude/ds";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { setPageTitle } from "@/components/app-shell/app-shell";
import { usePopper } from "@/hooks/use-popper";
import { tb } from "@/lib/tracking";

export const Route = createFileRoute("/_app/account/oidc")({
  beforeLoad() {
    setPageTitle("OpenID Settings");
  },
  component: OIDCPage,
});

const AttachOIDCFormSchema = z.object({
  issuer: z.enum(OIDCOptions, {
    errorMap: () => ({ message: "Please select a provider" }),
  }),
  client_id: z.string(),
  client_secret: z.string(),
  oidc_url: z.string(),
  email_attr: z.string(),
});

const OrganizationSlugFormSchema = z.object({
  slug: z.string(),
});

function OIDCPage() {
  const authConfig = useAuthConfig();
  const account = useQuery(queries.iam.getAccount(authConfig));
  const [showOIDCForm, setShowOIDCForm] = useState<boolean>(
    Boolean(account.data?.oidc?.issuer) === false,
  );

  return (
    <div className="flex h-full w-full flex-col gap-4 p-6">
      <div className={"flex min-h-[38px] items-center justify-between"}>
        <h1 className="min-h-[38px] text-2xl dark:font-light">
          OpenID Connect Settings
        </h1>
      </div>
      <div className="border-b border-stroke-line" />

      <div className="flex flex-col gap-0 py-4">
        <OrganizationSlug />
        {showOIDCForm ? (
          <OIDCForm setShowOIDCForm={setShowOIDCForm} />
        ) : (
          <FilledForm setShowOIDCForm={setShowOIDCForm} />
        )}
      </div>
    </div>
  );
}

function OrganizationSlug() {
  const [showForm, setShowForm] = useState<boolean>(false);
  const authConfig = useAuthConfig();
  const account = useQuery(queries.iam.getAccount(authConfig));

  if (showForm) {
    return (
      <OrganizationSlugForm
        onSuccess={() => {
          setShowForm(false);
        }}
        setShowForm={setShowForm}
      />
    );
  }

  return (
    <InputField label="Organization Slug">
      <div className="mb-4 flex items-center gap-1">
        <span className="text-sm">{account.data?.slug}</span>
        <EditorIcon
          role="button"
          onClick={() => {
            setShowForm(true);
          }}
          className="size-4 cursor-pointer text-secondary-foreground hover:fill-primary-foreground"
        />
      </div>
    </InputField>
  );
}

function OrganizationSlugForm({
  onSuccess,
  setShowForm,
}: {
  onSuccess: () => void;
  setShowForm: (b: boolean) => void;
}) {
  const authConfig = useAuthConfig();
  const { data: slug } = useQuery({
    ...queries.iam.getAccount(authConfig),
    select: (data) => data.slug,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (params: { slug: string }) => {
      await service(authConfig).iam.updateAccount(params);
    },
    onSuccess: async () => {
      notifySuccess({ message: "Organization slug successfully updated" });
      onSuccess();
    },
    onError: (e) => {
      notifyError({ message: "Failed to update organization slug", error: e });
    },
  });

  const form = useForm<z.infer<typeof OrganizationSlugFormSchema>>({
    defaultValues: {
      slug,
    },
    resolver: zodResolver(OrganizationSlugFormSchema),
  });

  const { register } = form;

  return (
    <Form
      {...form}
      className="mb-4 flex w-full gap-2 lg:w-96"
      onSubmitValid={(data) => {
        mutate(data);
      }}
    >
      <div className="flex w-full flex-col gap-2">
        <InputFormField
          label="Organization Slug"
          {...register("slug")}
          type="url"
          required
        />

        <div className="flex items-center gap-2">
          <Button
            loading={isPending}
            disabled={isPending}
            intent="primary"
            type="submit"
            size="small"
          >
            Save
          </Button>
          <Button
            loading={isPending}
            disabled={isPending}
            intent="secondary"
            type="button"
            size="small"
            onClick={() => setShowForm(false)}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Form>
  );
}

function getIssuerOptions(): SelectOption<string>[] {
  const defaultOption: SelectOption<string> = {
    label: "Select a provider",
    value: "",
    disabled: true,
  };

  const options = OIDCOptions.map((option): SelectOption<string> => {
    return { label: capitalize(option), value: option, disabled: false };
  });
  return [defaultOption, ...options];
}

const OIDCForm = ({
  setShowOIDCForm,
}: {
  setShowOIDCForm: (v: boolean) => void;
}) => {
  const authConfig = useAuthConfig();
  const account = useQuery(queries.iam.getAccount(authConfig));
  const oidcConfig = account.data?.oidc;

  const { mutate, isPending } = useMutation({
    mutationFn: async (params: AttachOIDC) => {
      await service(authConfig).iam.attachOIDC({
        ...params,
        email_attr: params.issuer === "azure" ? "upn" : params.email_attr,
      });
    },
    onSuccess: async () => {
      notifySuccess({ message: "OIDC successfully configured" });
      setShowOIDCForm(false);
    },
    onError: (e) => {
      notifyError({ message: "Failed to configure OIDC", error: e });
    },
  });
  const issuerOptions = getIssuerOptions();
  const form = useForm<z.infer<typeof AttachOIDCFormSchema>>({
    defaultValues: {
      email_attr: "email",
      issuer: oidcConfig?.issuer ?? OIDCOptions[0],
      client_id: oidcConfig?.client_id ?? "",
      oidc_url: oidcConfig?.oidc_url ?? "",
    },
    resolver: zodResolver(AttachOIDCFormSchema),
  });

  const { register } = form;

  return (
    <Form
      {...form}
      onSubmitValid={(data) => {
        mutate(data);
      }}
      className="flex w-full flex-col gap-2"
    >
      <SelectFormField
        {...register("issuer")}
        label="Provider"
        size="sm"
        required
        options={issuerOptions}
        className="w-full"
      />
      <PasswordFormField
        label="Client ID"
        {...register("client_id")}
        placeholder={"Enter your Client ID"}
        required
      />
      <PasswordFormField
        label="Client Secret"
        {...register("client_secret")}
        placeholder={"Enter your Client Secret"}
        required
      />
      <InputFormField
        label="Issuer URL"
        {...register("oidc_url")}
        type="url"
        placeholder={"Enter your Issuer URL"}
        required
      />

      <a
        href={
          "https://support.preludesecurity.com/docs/where-do-i-find-my-openid-credentials"
        }
        target={"_blank"}
        className="mb-6 flex cursor-pointer flex-row items-center justify-start gap-2 text-sm text-primary-foreground underline underline-offset-2 font-normal"
        rel="noreferrer"
      >
        <HelpIcon className="w-h h-4 text-secondary-foreground" />{" "}
        <span>Where do I find my OpenID credentials?</span>
      </a>

      <div className="flex items-center gap-2">
        <Button
          loading={isPending}
          disabled={isPending}
          intent="primary"
          type="submit"
          className={tb("save-oidc-settings")}
        >
          Save
        </Button>
        <Button
          loading={isPending}
          disabled={isPending}
          intent="secondary"
          type="button"
          size="small"
          onClick={() => setShowOIDCForm(false)}
        >
          Cancel
        </Button>
      </div>
    </Form>
  );
};

const FilledForm = ({
  setShowOIDCForm,
}: {
  setShowOIDCForm: (value: boolean) => void;
}) => {
  const authConfig = useAuthConfig();

  const account = useQuery(queries.iam.getAccount(authConfig));
  const dialog = usePopper();
  const detachOIDC = useMutation({
    mutationFn: async () => {
      await service(authConfig).iam.detachOIDC();
    },
    onSuccess: async () => {
      notifySuccess({ message: "OIDC successfully detached" });
      setShowOIDCForm(true);
    },
    onError: (e) => {
      notifyError({ message: "Failed to detach OIDC", error: e });
    },
  });

  const oidc = account.data?.oidc;

  return (
    <>
      <Dialog {...dialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Warning: Disconnecting OpenID Provider</DialogTitle>
          </DialogHeader>
          <DialogBody>
            This will remove any users from your account currently configured
            with OpenID.
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>
            <Button
              intent="negative"
              loading={detachOIDC.isPending}
              icon={<LinkIcon />}
              onClick={() => detachOIDC.mutate()}
            >
              Disconnect Provider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex w-full flex-col gap-4 lg:w-96">
        <InputField label="Provider">
          <span className="text-sm">{capitalize(oidc?.issuer ?? "")}</span>
        </InputField>

        <InputField label="Client ID">
          <span className="text-sm">{oidc?.client_id}</span>
        </InputField>

        <InputField label="Configuration URL">
          <span className="text-sm">{oidc?.oidc_url}</span>
        </InputField>

        <div className="mt-6 flex gap-4">
          <Button
            type="button"
            intent="negative"
            className={tb("detach-oidc")}
            onClick={() => dialog.on()}
          >
            Detach OIDC
          </Button>
          <Button
            type="button"
            intent="secondary"
            className={tb("update-oidc-settings")}
            onClick={() => setShowOIDCForm(true)}
          >
            Update OIDC settings
          </Button>
        </div>
      </div>
    </>
  );
};
